import React from 'react';
import EndpointCard from '../EndpointCard';
import CodeBlock from '../CodeBlock';

const Documents = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  return (
    <section className="section">
      <h2 className="section-title">Documents</h2>
      <p className="text-gray-300 mb-6 leading-7 max-w-[65ch]">
        Manage and retrieve documents within namespaces. Documents are uploaded to namespaces and
        automatically processed for semantic search and knowledge graph extraction.
      </p>

      <EndpointCard
        method="GET"
        path="/documents/{document_uuid}/download"
        title="Download Document"
        description="Downloads the original file for a specific document by its UUID."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            document_uuid: { required: true, description: 'The unique identifier of the document' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Path Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>document_uuid</code></td>
                  <td>string (UUID)</td>
                  <td><span className="required-badge">Required</span></td>
                  <td>The unique identifier of the document</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/documents/{document_uuid}/download" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  --output document.pdf`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <p className="text-sm text-gray-400 mb-2">Returns the raw file content with appropriate content-type headers.</p>
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="PUT"
        path="/documents/{document_uuid}"
        title="Update Document"
        description="Uploads a new version of an existing document and triggers re-ingestion. Returns a new ingestion task UUID to poll."
        tags={['Authentication: Required', 'Async: Yes']}
        parameters={{
          path: {
            document_uuid: { required: true, description: 'The unique identifier of the document to update' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Path Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>document_uuid</code></td>
                  <td>string (UUID)</td>
                  <td><span className="required-badge">Required</span></td>
                  <td>The unique identifier of the document to update</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <p className="text-sm text-gray-400 mb-2">Multipart form data with the file to upload.</p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X PUT "${BASE_URL}/documents/{document_uuid}" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -F "file=@/path/to/updated-document.pdf"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (202 Accepted):</h4>
            <CodeBlock code={`{
  "uuid": "{ingestion_task_uuid}",
  "document_uuid": "{document_uuid}",
  "status": "PENDING"
}`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="DELETE"
        path="/documents/{document_uuid}"
        title="Delete Document"
        description="Permanently deletes a document and all its associated chunks and ingestion tasks. This operation cannot be undone."
        tags={['Authentication: Required']}
        parameters={{
          path: {
            document_uuid: { required: true, description: 'The unique identifier of the document to delete' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Path Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>document_uuid</code></td>
                  <td>string (UUID)</td>
                  <td><span className="required-badge">Required</span></td>
                  <td>The unique identifier of the document to delete</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X DELETE "${BASE_URL}/documents/{document_uuid}" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "uuid": "{document_uuid}",
  "namespace_uuid": "{namespace_uuid}",
  "document_type": "PLAINTEXT",
  "status": "PENDING",
  "data": {
    "filename": "sample_document.txt"
  }
}`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Error Responses:</h4>
            <ul className="list-disc list-inside space-y-2 text-sm text-gray-400">
              <li><code className="bg-red-900/50 px-1 py-0.5 rounded">401</code> - API Key required</li>
              <li><code className="bg-red-900/50 px-1 py-0.5 rounded">403</code> - Invalid or missing API Key, or not authorized to access this document</li>
              <li><code className="bg-red-900/50 px-1 py-0.5 rounded">404</code> - Document not found</li>
              <li><code className="bg-red-900/50 px-1 py-0.5 rounded">422</code> - Validation error (invalid UUID format)</li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Notes:</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-400">
              <li>Deleting a document will cascade delete all associated document chunks and ingestion tasks</li>
              <li>This operation cannot be undone</li>
              <li>You must have access to the namespace containing the document to delete it</li>
            </ul>
          </div>
        </div>
      </EndpointCard>
    </section>
  );
};

export default Documents;
